import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FaBook, FaChartLine, FaUser, FaClipboardCheck, FaSpinner, FaCalendarAlt, FaFileAlt, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config/api';

const GuardianDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${config.apiURL}/guardian/dashboard`);
        setDashboardData(response.data);
      } catch (err) {
        console.error('Erro ao carregar dados do dashboard:', err);
        setError('Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Em Aberto':
        return 'bg-blue-100 text-blue-800';
      case 'Concluído':
        return 'bg-yellow-100 text-yellow-800';
      case 'Aprovado':
        return 'bg-green-100 text-green-800';
      case 'Atrasado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Calcular média geral das notas
  const calculateAverageGrade = () => {
    if (!dashboardData?.recent_feedbacks || dashboardData.recent_feedbacks.length === 0) {
      return '-';
    }
    
    const gradesWithValues = dashboardData.recent_feedbacks.filter(item => item.submission.grade !== null);
    if (gradesWithValues.length === 0) return '-';
    
    const average = gradesWithValues.reduce((sum, item) => sum + item.submission.grade, 0) / gradesWithValues.length;
    return average.toFixed(1);
  };

  // Calcular taxa de pontualidade
  const calculatePunctualityRate = () => {
    const total = dashboardData?.total_homeworks || 0;
    const onTime = Object.values(dashboardData?.submissions_by_status || {}).reduce((sum, count) => sum + count, 0) - (dashboardData?.submissions_by_status?.['Atrasado'] || 0);
    
    if (total === 0) return '-%';
    return Math.round((onTime / total) * 100) + '%';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Olá, {user?.name}! 👋
          </h1>
          <p className="mt-2 text-gray-600">
            Acompanhe o progresso educacional do seu filho - {dashboardData?.student?.name}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Lições</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData?.total_homeworks || 0}</p>
              </div>
              <FaBook className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Concluídas</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData?.submissions_by_status?.['Concluído'] || 0}</p>
              </div>
              <FaClipboardCheck className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Média Geral</p>
                <p className="text-2xl font-bold text-gray-900">{calculateAverageGrade()}</p>
              </div>
              <FaChartLine className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pontualidade</p>
                <p className="text-2xl font-bold text-gray-900">{calculatePunctualityRate()}</p>
              </div>
              <FaUser className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Status das Lições */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Em Aberto</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData?.submissions_by_status?.['Em Aberto'] || 0}</p>
              </div>
              <FaClock className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aprovadas</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData?.submissions_by_status?.['Aprovado'] || 0}</p>
              </div>
              <FaClipboardCheck className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Atrasadas</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData?.submissions_by_status?.['Atrasado'] || 0}</p>
              </div>
              <FaExclamationTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lições Próximas do Vencimento */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Lições Próximas do Vencimento
            </h2>
            {dashboardData?.upcoming_homeworks && dashboardData.upcoming_homeworks.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.upcoming_homeworks.map((item) => (
                  <div key={item.homework.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FaFileAlt className="w-5 h-5 text-blue-600" />
                      <div>
                        <h3 className="font-medium text-gray-900">{item.homework.title}</h3>
                        <p className="text-sm text-gray-600">{item.homework.subject}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(item.homework.due_date)}
                      </p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.submission.status)}`}>
                        {item.submission.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaCalendarAlt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Nenhuma lição próxima do vencimento</p>
                <p className="text-sm mt-2">
                  Parabéns! Todas as lições estão em dia.
                </p>
              </div>
            )}
          </div>

          {/* Feedbacks Recentes */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Feedbacks Recentes
            </h2>
            {dashboardData?.recent_feedbacks && dashboardData.recent_feedbacks.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.recent_feedbacks.map((item) => (
                  <div key={item.submission.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{item.homework.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">{item.homework.subject}</p>
                        {item.submission.feedback && (
                          <p className="text-sm text-gray-700 italic">"{item.submission.feedback}"</p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        {item.submission.grade && (
                          <p className="text-lg font-bold text-green-600">
                            {item.submission.grade}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {formatDate(item.submission.corrected_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaChartLine className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Nenhum feedback ainda</p>
                <p className="text-sm mt-2">
                  Feedbacks do professor aparecerão aqui
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Ações Rápidas */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Ações Rápidas
          </h2>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/homeworks"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <FaBook className="w-4 h-4" />
              <span>Ver Todas as Lições</span>
            </Link>
            
            <Link
              to="/analytics"
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <FaChartLine className="w-4 h-4" />
              <span>Relatórios Detalhados</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardianDashboard; 